import { useEffect, useState, type FormEvent } from "react";
import {
  ApiError,
  fetchHistory,
  saveVersion,
  versionExportUrl,
  type History,
  type VersionSummary,
} from "./api.js";
import styles from "./History.module.css";
import ui from "./Ui.module.css";
import { Modal } from "./ui/Modal.js";

/**
 * The map's history (Phase 3), for staff only: the versions saved when a workshop ended, the map
 * was finalised or a file was imported, or on request; and the event log from Salesforce. A version
 * downloads as a JSON export that the Value Map Builder can import.
 */

const WHEN = new Intl.DateTimeFormat("en-AU", {
  timeZone: "Australia/Brisbane",
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/** A Salesforce date-time in Brisbane time, e.g. "16 Oct 2026, 2:05 pm". */
export function when(iso: string): string {
  const d = new Date(iso.replace(/\+0000$/, "Z"));
  return Number.isNaN(d.getTime()) ? "" : WHEN.format(d);
}

const REASON: Record<VersionSummary["reason"], string> = {
  "Workshop closed": "Workshop ended",
  Finalised: "Finalised",
  Imported: "Before an import",
  Manual: "Saved by hand",
};

export function HistoryPanel({
  mapId,
  canSave,
  canDownload,
  onClose,
  onMessage,
}: {
  mapId: string;
  /** Owner and staff editors can save a version now. */
  canSave: boolean;
  /** People who can export can download a version. */
  canDownload: boolean;
  onClose: () => void;
  onMessage: (text: string) => void;
}) {
  const [history, setHistory] = useState<History | null | "failed">(null);
  const [version, setVersion] = useState(0);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctl = new AbortController();
    fetchHistory(mapId, ctl.signal).then(
      (h) => setHistory(h ?? "failed"),
      (err: unknown) => {
        if ((err as Error).name !== "AbortError") setHistory("failed");
      },
    );
    return () => ctl.abort();
  }, [mapId, version]);

  function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    saveVersion(mapId, label.trim() || undefined).then(
      (r) => {
        onMessage(`Version ${r.version} saved`);
        setLabel("");
        setBusy(false);
        setVersion((v) => v + 1);
      },
      (err: unknown) => {
        setError(err instanceof ApiError ? err.message : "The version could not be saved.");
        setBusy(false);
      },
    );
  }

  return (
    <Modal title="History" onClose={onClose} wide>
      {canSave && (
        <form onSubmit={save} className={styles.save} noValidate>
          <label htmlFor="history-label">Save a version now (optional name)</label>
          <div className={styles.saveRow}>
            <input
              id="history-label"
              value={label}
              maxLength={200}
              autoComplete="off"
              placeholder="e.g. Before the board meeting"
              onChange={(e) => setLabel(e.target.value)}
            />
            <button type="submit" className={ui.btn} disabled={busy}>
              Save version
            </button>
          </div>
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
        </form>
      )}

      {history === null && <p>Loading…</p>}
      {history === "failed" && (
        <p className={styles.error} role="alert">
          The history could not be loaded from Salesforce.
        </p>
      )}
      {history && history !== "failed" && (
        <>
          <h4 className={styles.heading}>Versions</h4>
          {history.versions.length === 0 ? (
            <p className={styles.empty}>
              No versions yet. One is saved when a workshop ends and when the map is finalised.
            </p>
          ) : (
            <table className={styles.table}>
              <caption className="visually-hidden">Saved versions, newest first</caption>
              <thead>
                <tr>
                  <th scope="col">Version</th>
                  <th scope="col">Why</th>
                  <th scope="col">When</th>
                  {canDownload && (
                    <th scope="col">
                      <span className="visually-hidden">Download</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {history.versions.map((v) => (
                  <tr key={v.id}>
                    <td>{v.number}</td>
                    <td>
                      {REASON[v.reason]}
                      {v.label && <span className={styles.label}>{v.label}</span>}
                    </td>
                    <td>{when(v.createdAt)}</td>
                    {canDownload && (
                      <td>
                        <a
                          className={styles.download}
                          href={versionExportUrl(mapId, v.id)}
                          download
                          aria-label={`Download version ${v.number}`}
                        >
                          Download
                        </a>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h4 className={styles.heading}>Activity</h4>
          {history.events.length === 0 ? (
            <p className={styles.empty}>Nothing recorded yet.</p>
          ) : (
            <ul className={styles.events} aria-label="Activity, newest first">
              {history.events.map((e, i) => (
                <li key={`${e.at}-${i}`}>
                  <span className={styles.when}>{when(e.at)}</span>
                  <span className={styles.what}>
                    {e.type}
                    {e.detail && `: ${e.detail}`}
                  </span>
                  <span className={styles.who}>{e.actorEmail}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div className={ui.row}>
        <button type="button" className={ui.btn} onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}
